import * as Yup from "yup";
import { FormikErrors, FormikValues } from "formik";

import { OK as HTTP_OK } from "@config/httpStatuses";
import { defaultValues } from "@config/defaultValues";
import {
  EOT_SQUARE_METERS_MIN_LIMIT,
  EOT_SQUARE_METERS_MAX_LIMIT,
  VALIDATE_FIELD_LIMIT_LONG,
  VALIDATE_FIELD_LIMIT_NORMAL,
} from "@config/consts";

import { ThunkActionDispatch } from "@typings/reduxThunkActions";
import {
  CHECKOUT_RESULT_NEXT_STEP,
  CHECKOUT_RESULT_STATUS,
  AVAILABLE_COUNTRIES,
} from "@typings/globals";
import { IOAuthState, OAUTH_PROVIDERS } from "@typings/oauth";
import { CHECKOUT_FORM, CHECKOUT_FORM_ERROR_MAP } from "@typings/booking";
import { DatatransPayload } from "@typings/datatrans";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { stringifyFalsies } from "@services/StringifyFalsies";
import { randomString } from "@services/RandomString";

import { TranslateFunction } from "@hooks/useTranslate";
import { SendEvent } from "@hooks/useTelemetry";

import { END_OF_TENANCY_EXTRA_SERVICES } from "@app/redux/endOfTenancy/endOfTenancy.typings";
import { fetchVoucher } from "@app/redux/endOfTenancy/endOfTenancy.actions";
import { IStore } from "@app/redux/reducers";
import { showGenericErrorToast } from "@app/containers/Toastify";

interface GetInitialValuesCheckoutProps {
  useRandomPassword?: boolean;
}

export const getInitialValuesCheckout = ({
  useRandomPassword,
}: GetInitialValuesCheckoutProps) => {
  const password = useRandomPassword
    ? randomString(defaultValues.misc.minPasswordLength)
    : "";

  return {
    email: "",
    password,
    passwordConfirmation: password,
    firstName: "",
    lastName: "",
    address: "",
    floorAndDoor: "",
    city: "",
    phoneNumber: "",
    entryCode: "",
    locationUuid: "",
    marketingConsent: false,
  };
};

enum END_OF_TENANCY_INFORMATION_ENUM {
  SQUARE_METERS = "squareMeters",
  REQUEST_DIFFERENT_STARTING_HOUR = "requestDifferentStartingHour",
  HAS_CLEANING_MATERIALS = "hasCleaningMaterials",
  SHOW_OFFER = "showOffer",
}

const initialValuesEndOfTenancyInformation = {
  squareMeters: defaultValues.endOfTenancy.squareMeters,
  showOffer: false,
  hasCleaningMaterials: false,
};

const squareMetersSchema = (
  country: AVAILABLE_COUNTRIES,
  translate: TranslateFunction,
) =>
  Yup.object().shape({
    [END_OF_TENANCY_INFORMATION_ENUM.SQUARE_METERS]: Yup.number()
      .required(translate("batmaid_pwa_end_of_tenancy.square_meters_required"))
      .min(
        EOT_SQUARE_METERS_MIN_LIMIT,
        translate(
          country === AVAILABLE_COUNTRIES.LU
            ? "batmaid_pwa_end_of_tenancy.square_meters_min_limit_exceeded_lu"
            : "batmaid_pwa_end_of_tenancy.square_meters_min_limit_exceeded_ch",
        ),
      )
      .test(
        "maxOrEquals",
        translate(
          country === AVAILABLE_COUNTRIES.LU
            ? "batmaid_pwa_end_of_tenancy.square_meters_max_limit_exceeded_lu"
            : "batmaid_pwa_end_of_tenancy.square_meters_max_limit_exceeded_ch",
        ),
        value => value <= EOT_SQUARE_METERS_MAX_LIMIT,
      ),
  });

const showOfferSchema = (translate: TranslateFunction) =>
  Yup.object().shape({
    [END_OF_TENANCY_INFORMATION_ENUM.SHOW_OFFER]: Yup.boolean()
      .required(translate("batmaid_pwa_end_of_tenancy.get_a_quote_required"))
      .oneOf(
        [true],
        translate("batmaid_pwa_end_of_tenancy.get_a_quote_required"),
      ),
  });

const cleaningMaterialSchema = (translate: TranslateFunction) =>
  Yup.object().shape({
    [END_OF_TENANCY_INFORMATION_ENUM.HAS_CLEANING_MATERIALS]: Yup.boolean()
      .required(
        translate("batmaid_pwa_end_of_tenancy.cleaning_materials_required"),
      )
      .oneOf(
        [true],
        translate("batmaid_pwa_end_of_tenancy.cleaning_materials_required"),
      ),
  });

const endOfTenancyInformationSchema = (
  country: AVAILABLE_COUNTRIES,
  translate: TranslateFunction,
): Yup.ObjectSchema =>
  Yup.object()
    .concat(squareMetersSchema(country, translate))
    .concat(cleaningMaterialSchema(translate))
    .concat(showOfferSchema(translate));

const registerSchema = (translate: TranslateFunction) =>
  Yup.object().shape({
    [CHECKOUT_FORM.EMAIL]: Yup.string()
      .email(translate("batmaid_pwa_generic.email_wrong_format"))
      .required(translate("batmaid_pwa_generic.email_required"))
      .max(
        VALIDATE_FIELD_LIMIT_LONG,
        translate("batmaid_pwa_generic.validate_too_long", {
          maxValue: VALIDATE_FIELD_LIMIT_LONG,
        }),
      ),
    [CHECKOUT_FORM.PASSWORD]: Yup.string()
      .min(
        defaultValues.misc.minPasswordLength,
        translate("batmaid_pwa_end_of_tenancy.password_min_length"),
      )
      .required(translate("batmaid_pwa_end_of_tenancy.password_required")),
    [CHECKOUT_FORM.PASSWORD_CONFIRMATION]: Yup.string()
      .min(
        defaultValues.misc.minPasswordLength,
        translate("batmaid_pwa_end_of_tenancy.password_min_length"),
      )
      .oneOf(
        [Yup.ref(CHECKOUT_FORM.PASSWORD), null],
        translate("batmaid_pwa_end_of_tenancy.password_confirmation_match"),
      )
      .required(
        translate("batmaid_pwa_end_of_tenancy.password_confirmation_required"),
      ),
  });

const addressSchema = (translate: TranslateFunction) =>
  Yup.object().shape({
    [CHECKOUT_FORM.ADDRESS]: Yup.string()
      .trim()
      .required(translate("batmaid_pwa_end_of_tenancy.address_required"))
      .max(
        VALIDATE_FIELD_LIMIT_LONG,
        translate("batmaid_pwa_generic.validate_too_long", {
          maxValue: VALIDATE_FIELD_LIMIT_LONG,
        }),
      ),
    [CHECKOUT_FORM.FLOOR_AND_DOOR]: Yup.string().max(
      VALIDATE_FIELD_LIMIT_NORMAL,
      translate("batmaid_pwa_generic.validate_too_long", {
        maxValue: VALIDATE_FIELD_LIMIT_NORMAL,
      }),
    ),
    [CHECKOUT_FORM.ENTRY_CODE]: Yup.string().max(
      255,
      translate("batmaid_pwa_end_of_tenancy.entry_code_max_length"),
    ),
  });

const userDataSchema = (translate: TranslateFunction) =>
  Yup.object()
    .shape({
      [CHECKOUT_FORM.FIRST_NAME]: Yup.string()
        .trim()
        .required(translate("batmaid_pwa_end_of_tenancy.first_name_required"))
        .max(
          VALIDATE_FIELD_LIMIT_NORMAL,
          translate("batmaid_pwa_generic.validate_too_long", {
            maxValue: VALIDATE_FIELD_LIMIT_NORMAL,
          }),
        ),
      [CHECKOUT_FORM.LAST_NAME]: Yup.string()
        .trim()
        .required(translate("batmaid_pwa_end_of_tenancy.last_name_required"))
        .max(
          VALIDATE_FIELD_LIMIT_NORMAL,
          translate("batmaid_pwa_generic.validate_too_long", {
            maxValue: VALIDATE_FIELD_LIMIT_NORMAL,
          }),
        ),
      [CHECKOUT_FORM.PHONE_NUMBER]: Yup.string()
        .required(translate("batmaid_pwa_end_of_tenancy.phone_number_required"))
        .max(
          VALIDATE_FIELD_LIMIT_NORMAL,
          translate("batmaid_pwa_generic.validate_too_long", {
            maxValue: VALIDATE_FIELD_LIMIT_NORMAL,
          }),
        ),
    })
    .concat(addressSchema(translate));

const locationSchema = (translate: TranslateFunction) =>
  Yup.object().shape({
    [CHECKOUT_FORM.LOCATION_UUID]: Yup.string().required(
      translate("batmaid_pwa_end_of_tenancy.address_required"),
    ),
  });

const anonymousUserCheckoutSchema = (translate: TranslateFunction) =>
  Yup.object()
    .concat(registerSchema(translate))
    .concat(userDataSchema(translate));

const loggedUserCheckoutSchema = (translate: TranslateFunction) =>
  Yup.object().concat(locationSchema(translate));

enum CHECKOUT_REQUEST_ERRORS {
  RESERVATION_EXPIRED = "RESERVATION_EXPIRED",
  INVALID_VOUCHER_TOKEN = "INVALID_VOUCHER_TOKEN",
}

interface CheckoutSubmitHandler {
  onRegisterClient: () => (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ) => Promise<AsyncActionResult>;
  onCheckout: () => (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ) => Promise<AsyncActionResult>;
  dispatch: ThunkActionDispatch;
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  connectUser: (
    provider: OAUTH_PROVIDERS,
    accessToken: string,
  ) => Promise<AsyncActionResult>;
  setRedirectUrl: (redirectUrl: string) => void;
  setTransactionId: (transactionId: string | null) => void;
  setDataTransPayload: (dataTransPayload: DatatransPayload) => void;
  setNextStep: (nextStep: CHECKOUT_RESULT_NEXT_STEP) => void;
  setCheckoutStatus: (status: CHECKOUT_RESULT_STATUS) => void;
  isUserLoggedIn: boolean;
  oauthUserData: IOAuthState;
  fetchUserData: () => Promise<AsyncActionResult>;
  fetchLocations: () => (
    dispatch: ThunkActionDispatch,
  ) => Promise<AsyncActionResult>;
  translate: (key: string) => string;
  goToEndOfTenancyPage: () => void;
  resetVoucher: () => void;
  sendEvent: SendEvent;
  voucher?: string;
}

const checkoutSubmitHandler = async ({
  onRegisterClient,
  onCheckout,
  dispatch,
  setErrors,
  setSubmitting,
  connectUser,
  setRedirectUrl,
  setTransactionId,
  setDataTransPayload,
  setNextStep,
  setCheckoutStatus,
  isUserLoggedIn,
  oauthUserData,
  fetchUserData,
  fetchLocations,
  translate,
  goToEndOfTenancyPage,
  resetVoucher,
  sendEvent,
  voucher,
}: CheckoutSubmitHandler): Promise<void> => {
  setSubmitting(true);

  if (!isUserLoggedIn) {
    const registerResp = await dispatch<Promise<AsyncActionResult>>(
      onRegisterClient(),
    );

    if (registerResp.error) {
      setErrors({
        [CHECKOUT_FORM_ERROR_MAP[
          registerResp.error as keyof typeof CHECKOUT_FORM_ERROR_MAP
        ]]: String(registerResp.translatedErrorMessage),
      });
      setSubmitting(false);
      return;
    }

    if (!registerResp.error && registerResp.status !== HTTP_OK) {
      showGenericErrorToast(
        translate("batmaid_pwa_generic.registration_failed"),
      );
      setSubmitting(false);
      return;
    }

    const userDataResp = await fetchUserData();
    const locationsResp = await dispatch<Promise<AsyncActionResult>>(
      fetchLocations(),
    );

    if (userDataResp.error || locationsResp.error) {
      showGenericErrorToast();
      setSubmitting(false);
      return;
    }

    if ((await registerResp) && oauthUserData.accessToken) {
      const connectResp = await connectUser(
        oauthUserData.provider,
        stringifyFalsies(oauthUserData.accessToken),
      );

      if (connectResp.status !== HTTP_OK) {
        showGenericErrorToast(
          translate("batmaid_pwa_generic.registration_failed"),
        );
        setSubmitting(false);
        return;
      }
    }

    if (voucher) {
      const voucherResp = await dispatch(fetchVoucher(voucher));

      if (voucherResp.error) {
        showGenericErrorToast(
          voucherResp.translatedErrorMessage ||
            translate("batmaid_pwa_generic.something_went_wrong"),
        );
        setSubmitting(false);
        sendEvent("voucher-checking-failed", {
          context: {
            voucher,
            place: "eot-checkout",
          },
        });
        return;
      }
    }
  }

  const checkoutResp = await dispatch<Promise<AsyncActionResult>>(onCheckout());

  checkoutResp.onError(err => {
    const errMessage =
      err.message || translate("batmaid_pwa_generic.something_went_wrong");

    showGenericErrorToast(errMessage);
    setSubmitting(false);

    if (err.enum === CHECKOUT_REQUEST_ERRORS.RESERVATION_EXPIRED) {
      goToEndOfTenancyPage();
    }

    if (err.enum === CHECKOUT_REQUEST_ERRORS.INVALID_VOUCHER_TOKEN) {
      sendEvent("voucher-checking-failed", {
        context: {
          voucher,
          place: "eot-checkout",
        },
      });
      dispatch(resetVoucher());
    }
  });

  setSubmitting(false);

  checkoutResp.mapApiErrorsToForm(setErrors).onSuccess(() => {
    setRedirectUrl(checkoutResp.payload.data.datatransInstruction.href);
    setTransactionId(
      checkoutResp.payload.data.datatransInstruction.transactionId,
    );
    setDataTransPayload(checkoutResp.payload.data.datatransInstruction.payload);
    setNextStep(checkoutResp.payload.data.nextStep);
    setCheckoutStatus(checkoutResp.payload.data.datatransInstruction.status);
  });
};

const getEndOfTenancyExtraServiceTranslation = (
  service: END_OF_TENANCY_EXTRA_SERVICES,
): string => {
  switch (service) {
    case END_OF_TENANCY_EXTRA_SERVICES.INSIDE_FRIDGE:
      return "batmaid_pwa_end_of_tenancy.extra_service_inside_fridge";
    case END_OF_TENANCY_EXTRA_SERVICES.INSIDE_OVEN:
      return "batmaid_pwa_end_of_tenancy.extra_service_inside_oven";
    case END_OF_TENANCY_EXTRA_SERVICES.DOOR_HANDLES:
      return "batmaid_pwa_end_of_tenancy.extra_service_door_handles";
    case END_OF_TENANCY_EXTRA_SERVICES.BASEBOARDS:
      return "batmaid_pwa_end_of_tenancy.extra_service_baseboards";
    case END_OF_TENANCY_EXTRA_SERVICES.INTERIOR_WINDOWS:
      return "batmaid_pwa_end_of_tenancy.extra_service_interior_windows";
    case END_OF_TENANCY_EXTRA_SERVICES.INSIDE_CABINETS:
      return "batmaid_pwa_end_of_tenancy.extra_service_inside_cabinets";
    case END_OF_TENANCY_EXTRA_SERVICES.WINDOW_BLINDS:
      return "batmaid_pwa_end_of_tenancy.extra_service_window_blinds";
    case END_OF_TENANCY_EXTRA_SERVICES.BALCONY:
      return "batmaid_pwa_end_of_tenancy.extra_service_balcony";
    default:
      return `batmaid_pwa_end_of_tenancy.extra_service_${service}`;
  }
};

export const EndOfTenancyProcessHelper = {
  anonymousUserCheckoutSchema,
  loggedUserCheckoutSchema,
  checkoutSubmitHandler,
  cleaningMaterialSchema,
  getInitialValuesCheckout,
  initialValuesEndOfTenancyInformation,
  END_OF_TENANCY_INFORMATION_ENUM,
  endOfTenancyInformationSchema,
  getEndOfTenancyExtraServiceTranslation,
};
